import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassBadge - Badge component with glass morphism effects
 *
 * A versatile badge component for displaying status, categories, and labels
 * with glass morphism styling. Includes specialized variants for EPIC statuses
 * and milestone types.
 *
 * @example
 * ```tsx
 * <GlassBadge variant="success">Active</GlassBadge>
 * <GlassBadge status="committed">Committed</GlassBadge>
 * <GlassBadge milestoneType="business">Business Milestone</GlassBadge>
 * ```
 */

export interface GlassBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** General badge variant */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /** EPIC status-specific variant (overrides variant) */
  status?: 'committed' | 'stretch' | 'draft' | 'prioritized';
  /** Milestone type variant (overrides variant and status) */
  milestoneType?: 'business' | 'tech';
  /** Badge size */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Badge content */
  children: React.ReactNode;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

const variantClasses = {
  default: cn(
    'bg-[var(--surface-muted)]',
    'text-muted-foreground',
    'border border-border'
  ),
  primary: cn(
    'bg-[var(--status-info-bg)]',
    'text-[var(--status-info-text)]',
    'border border-[var(--status-info)]/30'
  ),
  success: cn(
    'bg-[var(--status-success-bg)]',
    'text-[var(--status-success-text)]',
    'border border-[var(--status-success)]/30'
  ),
  warning: cn(
    'bg-[var(--status-warning-bg)]',
    'text-[var(--status-warning-text)]',
    'border border-[var(--status-warning)]/30'
  ),
  danger: cn(
    'bg-[var(--status-error-bg)]',
    'text-[var(--status-error-text)]',
    'border border-[var(--status-error)]/30'
  ),
  info: cn(
    'bg-[var(--accent-cyan-soft,var(--status-info-bg))]',
    'text-[var(--accent-cyan,var(--status-info-text))]',
    'border border-[var(--accent-cyan,var(--status-info))]/30'
  ),
};

/**
 * GlassBadge Component
 *
 * Features:
 * - Six general variants (default, primary, success, warning, danger, info)
 * - Four status variants for EPIC states (committed, stretch, draft, prioritized)
 * - Two milestone type variants (business, tech)
 * - Three size options (sm, md, lg)
 * - Glass morphism effects with backdrop blur
 * - Theme-aware (light/dark mode support)
 * - Accessible with semantic HTML
 *
 * Priority: milestoneType > status > variant
 */
export const GlassBadge = forwardRef<HTMLSpanElement, GlassBadgeProps>(
  (
    {
      variant = 'default',
      status,
      milestoneType,
      size = 'md',
      className,
      children,
      ...props
    },
    ref
  ) => {
    // Determine which styling to apply (priority: milestoneType > status > variant)
    let badgeClasses: string;

    if (milestoneType) {
      // Use milestone type classes from design tokens
      badgeClasses = cn(
        'inline-flex items-center',
        'font-medium rounded-md',
        'backdrop-filter backdrop-blur-sm',
        sizeClasses[size],
        milestoneType === 'business' && 'milestone-business',
        milestoneType === 'tech' && 'milestone-tech',
        className
      );
    } else if (status) {
      // Use status classes from design tokens
      badgeClasses = cn(
        'inline-flex items-center',
        'font-medium rounded-md',
        'backdrop-filter backdrop-blur-sm',
        sizeClasses[size],
        status === 'committed' && 'status-committed',
        status === 'stretch' && 'status-stretch',
        status === 'draft' && 'status-draft',
        status === 'prioritized' && 'status-prioritized',
        className
      );
    } else {
      // Use general variant classes
      badgeClasses = cn(
        'inline-flex items-center',
        'font-medium rounded-md',
        'backdrop-filter backdrop-blur-sm',
        sizeClasses[size],
        variantClasses[variant],
        className
      );
    }

    return (
      <span ref={ref} className={badgeClasses} {...props}>
        {children}
      </span>
    );
  }
);

GlassBadge.displayName = 'GlassBadge';
